import { trpc } from "@/utils/trpc";
import { Fragment, type ReactNode } from "react";
import { useLocation } from "wouter";
import { ArrowLeft } from "lucide-react";

/** Breadcrumb back-bar shared across every QA screen — matches the ticket-detail
 *  pattern: a round ArrowLeft button + a clickable path. The arrow navigates to
 *  `backTo`; each crumb (except the last) navigates to its `to`. */
export function QaCrumb({ backTo, items, className }: { backTo: string; items: { label: string; to?: string; mono?: boolean }[]; className?: string }) {
  const [, setLocation] = useLocation();
  return (
    <div className={`flex items-center gap-2 mt-5 mb-2.5 -ml-2 ${className ?? ""}`}>
      <button
        onClick={() => setLocation(backTo)}
        aria-label="Back"
        className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-[#6b6386] hover:bg-[#f5f3ff] hover:text-[#6d28d9] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
      </button>
      <div className="flex items-center gap-2 text-[13px] text-[#6b6386] font-medium flex-wrap">
        {items.map((it, i) => {
          const last = i === items.length - 1;
          return (
            <Fragment key={i}>
              {i > 0 && <span className="text-[#b8b2c9]">/</span>}
              {it.to && !last ? (
                <span className="cursor-pointer hover:text-[#6d28d9]" onClick={() => setLocation(it.to!)}>{it.label}</span>
              ) : (
                <span className={`${last ? "text-[#1a1530] font-semibold" : ""} ${it.mono ? "font-mono" : ""}`}>{it.label}</span>
              )}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

/** QA module top bar — "QA Module" brand + Dashboard / Visits / Templates tabs.
 *  Sits above the hero on every QA screen; active tab follows the route. */
export function QaTopBar() {
  const [loc, navigate] = useLocation();
  const tab = loc.startsWith("/qa/visits") ? "visits" : "dash";
  return (
    <div className="qa-topbar">
      <button className={tab === "dash" ? "active" : ""} onClick={() => navigate("/qa")}>Dashboard</button>
      <button className={tab === "visits" ? "active" : ""} onClick={() => navigate("/qa/visits")}>Visits</button>
    </div>
  );
}

/**
 * STC Forward brand palette (from the GMS questionnaire design).
 * Centralised so all QA pages share the exact tokens.
 */
export const QA = {
  purple: "#4F008C",
  purpleDark: "#36006B",
  purpleDeep: "#200048",
  purpleMid: "#7B2FBE",
  purpleSoft: "#A855D8",
  purplePale: "#EDE5F5",
  purpleXPale: "#F7F0FD",
  green: "#00A651",
  greenPale: "#E6F7EF",
  red: "#D0021B",
  redPale: "#FDECEA",
  amber: "#E8930A",
  amberPale: "#FDF3E3",
  blue: "#1D4ED8",
  bluePale: "#E8F0FE",
  gray: "#6B7280",
  grayPale: "#F3F4F6",
  border: "#ECE7F3",
  borderMid: "#D9CFEA",
  text: "#1A1025",
  text2: "#4B4060",
  text3: "#8B7DA8",
  bg: "#F6F3FB",
  surface: "#FFFFFF",
} as const;

/** Cycle of purples for domain weight visualisations (stacked bars, legends). */
export const DOMAIN_COLORS = ["#4F008C", "#7B2FBE", "#A855D8", "#C77DEB", "#E0B9F5", "#6D28D9", "#9333EA"];

/** Design-C gradient hero banner — full-bleed across the app shell's content area. */
export function QaHero({ title, subtitle, actions, overlap }: { title: string; subtitle?: string; actions?: ReactNode; overlap?: boolean }) {
  return (
    <div
      className="relative -mx-4 overflow-hidden px-6 pt-8 text-white md:-mx-6 md:px-10"
      style={{
        background: `linear-gradient(135deg, ${QA.purpleDeep}, ${QA.purple} 55%, ${QA.purpleMid})`,
        borderRadius: "0 0 28px 28px",
        paddingBottom: overlap ? 72 : 34,
      }}
    >
      <span className="pointer-events-none absolute -right-16 -top-16 h-72 w-72 rounded-full" style={{ background: "rgba(255,255,255,.06)" }} />
      <span className="pointer-events-none absolute -bottom-24 right-24 h-52 w-52 rounded-full" style={{ background: "rgba(255,255,255,.05)" }} />
      <div className="relative z-10 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[28px] font-extrabold tracking-tight">{title}</h1>
          {subtitle && <p className="mt-1 max-w-[460px] text-[14px]" style={{ opacity: 0.8 }}>{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap gap-2.5">{actions}</div>}
      </div>
    </div>
  );
}

export function HeroBtnLight({ onClick, children }: { onClick?: () => void; children: ReactNode }) {
  return (
    <button onClick={onClick} className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-[13px] font-bold" style={{ color: QA.purple }}>
      {children}
    </button>
  );
}
export function HeroBtnGlass({ onClick, children }: { onClick?: () => void; children: ReactNode }) {
  return (
    <button onClick={onClick} className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-bold text-white" style={{ background: "rgba(255,255,255,.14)", border: "1px solid rgba(255,255,255,.25)" }}>
      {children}
    </button>
  );
}

export type QaVisitStatus =
  | "scheduled"
  | "questionnaire_submitted"
  | "under_review"
  | "assessment_submitted"
  | "report_approved"
  | "cancelled";

export const VISIT_STATUS_META: Record<QaVisitStatus, { label: string; color: string; bg: string }> = {
  scheduled: { label: "Scheduled", color: QA.purpleMid, bg: QA.purpleXPale },
  questionnaire_submitted: { label: "Questionnaire Submitted", color: "#1D4ED8", bg: "#E8F0FE" },
  under_review: { label: "Under Review", color: QA.amber, bg: QA.amberPale },
  assessment_submitted: { label: "Assessment Submitted", color: QA.purple, bg: QA.purplePale },
  report_approved: { label: "Report Approved", color: QA.green, bg: QA.greenPale },
  cancelled: { label: "Cancelled", color: QA.gray, bg: QA.grayPale },
};

export const BAND_META: Record<string, { label: string; color: string }> = {
  excellent: { label: "Excellent Operational Maturity", color: QA.green },
  strong: { label: "Strong Compliance", color: "#1D4ED8" },
  moderate: { label: "Moderate Improvement Required", color: QA.amber },
  high_risk: { label: "High Operational Risk", color: QA.red },
};

export const RATING_META: Record<string, { label: string; short: string; color: string; bg: string; score: string }> = {
  compliant: { label: "Compliant", short: "C", color: QA.green, bg: QA.greenPale, score: "1.0" },
  partially_compliant: { label: "Partially Compliant", short: "PC", color: QA.amber, bg: QA.amberPale, score: "0.8" },
  non_compliant: { label: "Non-Compliant", short: "NC", color: QA.red, bg: QA.redPale, score: "0" },
  not_applicable: { label: "Not Applicable", short: "N/A", color: QA.gray, bg: QA.grayPale, score: "—" },
};

export function StatusBadge({ status }: { status: QaVisitStatus }) {
  const meta = VISIT_STATUS_META[status] ?? VISIT_STATUS_META.scheduled;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold tracking-wide"
      style={{ color: meta.color, background: meta.bg }}
    >
      {meta.label}
    </span>
  );
}

export function formatDate(d?: string | Date | null): string {
  if (!d) return "—";
  const date = typeof d === "string" ? new Date(d.length <= 10 ? `${d}T00:00:00` : d) : d;
  if (isNaN(date.getTime())) return String(d);
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** Reads a File into a base64 data URL for tRPC upload. */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const EVIDENCE_ACCEPT =
  ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

/** Coarse QA permission booleans for client-side UX gating (server still enforces). */
export function useQaPerms() {
  const { data } = trpc.users.getMyPermissions.useQuery(undefined, { staleTime: 60_000 });
  const qa = (data as any)?.qa ?? {};
  return {
    roleCode: (data as any)?._roleCode as string | null | undefined,
    templateView: !!qa.templateView,
    templateManage: !!qa.templateManage,
    visitView: !!qa.visitView,
    visitManage: !!qa.visitManage,
    questionnaireView: !!qa.questionnaireView,
    questionnaireEdit: !!qa.questionnaireEdit,
    questionnaireSubmit: !!qa.questionnaireSubmit,
    reviewView: !!qa.reviewView,
    reviewManage: !!qa.reviewManage,
    assessmentView: !!qa.assessmentView,
    assessmentEdit: !!qa.assessmentEdit,
    assessmentSubmit: !!qa.assessmentSubmit,
    reportView: !!qa.reportView,
    reportApprove: !!qa.reportApprove,
  };
}

/** Opens a stored evidence file in a new tab via a signed URL. */
export function useOpenEvidence() {
  const utils = trpc.useUtils();
  return async (fileKey: string) => {
    try {
      const { url } = await utils.qa.questionnaire.getEvidenceUrl.fetch({ fileKey });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      /* ignore */
    }
  };
}
